<template>
  <div class="modal fade edit-plot-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark">
          <h5 class="modal-title fw-bold">
            <i class="mdi mdi-pencil me-2"></i>
            Edit Plot - {{ localPlot.plotNumber }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <!-- Plot Details -->
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Plot Number</label>
                <input v-model="localPlot.plotNumber" type="text" class="form-control bg-light" readonly>
                <small class="text-muted">Plot number cannot be changed</small>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Zone/Market</label>
                <input v-model="localPlot.zone" type="text" class="form-control bg-light" readonly>
                <small class="text-muted">Zone cannot be changed</small>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Owner Name <span class="text-danger">*</span></label>
                <input v-model="localPlot.ownerNames" type="text" class="form-control" placeholder="Enter owner name">
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">ID Number</label>
                <input v-model="localPlot.ownerID" type="text" class="form-control" placeholder="Enter ID number">
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Phone Number</label>
                <input v-model="localPlot.ownerPhoneNo" type="tel" class="form-control" placeholder="e.g., 0712345678">
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Email</label>
                <input v-model="localPlot.ownerEmail" type="email" class="form-control" placeholder="Enter email">
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Annual Rent (KES) <span class="text-danger">*</span></label>
                <input v-model.number="localPlot.annualRent" type="number" step="0.01" class="form-control" placeholder="0.00">
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Plot Size</label>
                <input v-model="localPlot.plotSize" type="text" class="form-control" placeholder="e.g., 3x3m">
              </div>
            </div>

            <div class="col-12">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Address</label>
                <textarea v-model="localPlot.plotAddress" class="form-control" rows="2" placeholder="Plot location"></textarea>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label fw-semibold">Status</label>
                <select v-model="localPlot.status" class="form-select">
                  <option value="Active">Active</option>
                  <option value="Vacant">Vacant</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Current Balance Display -->
          <div class="alert alert-info">
            <div class="row text-center">
              <div class="col-md-3">
                <small class="text-muted d-block">Arrears B/F</small>
                <strong>KES {{ numberFormat(localPlot.arrearsForward) }}</strong>
              </div>
              <div class="col-md-3">
                <small class="text-muted d-block">Total Billed</small>
                <strong>KES {{ numberFormat(localPlot.totalBilled) }}</strong>
              </div>
              <div class="col-md-3">
                <small class="text-muted d-block">Penalty</small>
                <strong>KES {{ numberFormat(localPlot.accumulatedPenalty) }}</strong>
              </div>
              <div class="col-md-3">
                <small class="text-muted d-block">Current Balance</small>
                <strong :class="localPlot.currentBalance > 0 ? 'text-danger' : 'text-success'">
                  KES {{ numberFormat(localPlot.currentBalance) }}
                </strong>
              </div>
            </div>
            <small class="text-muted d-block mt-2">
              <i class="mdi mdi-information me-1"></i>
              Financial balances are managed through billing and payment operations.
            </small>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button @click="handleUpdate" type="button" class="btn btn-warning" :disabled="updating">
            <span v-if="updating">
              <span class="spinner-border spinner-border-sm me-2"></span>Updating...
            </span>
            <span v-else>
              <i class="mdi mdi-check me-2"></i>Update Plot
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, plotRent } from "@/api";

export default {
  name: 'EditPlotModal',
  props: {
    plot: { type: Object, default: () => ({}) },
    subCounties: { type: Array, default: () => [] },
    wards: { type: Array, default: () => [] },
    zones: { type: Array, default: () => [] }
  },
  emits: ['get-wards', 'plot-updated'],
  data() {
    return {
      localPlot: {},
      updating: false
    }
  },
  watch: {
    plot: {
      handler(newVal) {
        this.localPlot = { ...newVal };
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    handleUpdate() {
      if (!this.localPlot.ownerNames) {
        alert("Owner name is required");
        return;
      }

      if (!this.localPlot.annualRent || this.localPlot.annualRent <= 0) {
        alert("Annual rent must be greater than 0");
        return;
      }

      this.updating = true;

      const data = new FormData();
      data.append("function", "updatePlot");
      data.append("id", this.localPlot.id);
      data.append("ownerNames", this.localPlot.ownerNames);
      data.append("ownerID", this.localPlot.ownerID || '');
      data.append("ownerPhoneNo", this.localPlot.ownerPhoneNo || '');
      data.append("ownerEmail", this.localPlot.ownerEmail || '');
      data.append("annualRent", this.localPlot.annualRent);
      data.append("plotSize", this.localPlot.plotSize || '');
      data.append("plotAddress", this.localPlot.plotAddress || '');
      data.append("status", this.localPlot.status || 'Active');
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, plotRent)
          .then((res) => {
            this.updating = false;
            if (res.data.success) {
              alert("Plot updated successfully");
              this.$emit('plot-updated');
              this.closeModal();
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            this.updating = false;
            alert("Error: " + e.message);
          });
    },

    closeModal() {
      const modal = document.querySelector('.edit-plot-modal');
      if (modal) {
        const instance = bootstrap.Modal.getInstance(modal);
        if (instance) instance.hide();
      }
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    }
  }
}
</script>